import * as Sentry from "@sentry/react";

import { BackendProductInfo } from "Components/Features/CheckPrice/Products/productConstants";
import { ProductProps } from "Components/Features/CheckPrice/Products/productInterfaces";
import { ProductBackendName } from "Components/Features/CheckPrice/Products/productTypes";
import { ProductPriceTile } from "Components/Features/CheckPrice/Tiles/ProductPriceTile";
import { DittoCoverageCard } from "Components/Features/CheckPrice/Tiles/ProductPriceTile/Coverages/DittoCoverages/DittoCoverageCard";
import { coveragePriceDifference } from "Components/Features/CheckPrice/Tiles/ProductPriceTile/Coverages/coverages.helpers";
import { StyledComparisonWrap } from "Components/Features/CheckPrice/Tiles/ProductPriceTile/productPriceTile.styles";
import { BackendErrorAlert } from "Components/Form/CommonFormElements/BackendErrorAlert/BackendErrorAlert";
import { ErrorsSummedUp } from "Components/Form/ErrorsSummedUp";
import { InputFailureTracker } from "Components/InputFailureTracker";
import { NavigationButtons } from "Components/NavigationButtons";
import {
  getCollectionOfDittoComponents,
  useTexts,
} from "Components/TextContext";
import { TextComponentNameLevel1 } from "Components/TextContext/textConstants";
import { Data } from "Components/TextContext/textInterfaces";
import { useAddProductToCartHandler } from "Store/addProductToCart/hooks";
import { HouseContentsCoverage } from "Store/interfaces/products/coverage";
import { INSURANCE_NAME_VALUE } from "Store/interfaces/products/index";
import { useFormState } from "Store/priceCalculation/priceCalculationHooks";
import { updatePriceCalculationFormState } from "Store/priceCalculation/priceCalculationSlice";
import { ReduxState } from "Store/rootReducer";
import { mapValidationErrorsDitto } from "Utils/formUtils";
import {
  getDeductibleOptions,
  getDefaultDeductibleForProduct,
} from "Utils/priceUtils";
import { Form, FormikProps } from "formik";
import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { HOUSE_CONTENTS_INTRO_STEP, HOUSE_CONTENTS_PRICE_STEP } from "../";
import { ChooseDeductible } from "../../FormComponentsHouseProducts/ChooseDeductible";
import { StyledHouseH3 } from "../../houseProductsStyled";
import { getCoveragesFilteredOnStandardSuper } from "../../houseProductsUtils";
import {
  HouseContentsProductIntroFormValues,
  HouseContentsProductPriceFormValues,
} from "../houseContentsFormValuesInterfaces";
import {
  getInitialFormStateWithCorrectCoverage,
  isFullRental,
} from "../houseContentsUtils";
import { HouseContentsPriceElements } from "./HouseContentsPriceElements";
import { SafetyMeasureHouseContentsQuestions } from "./SafetyMeasureHouseContentsQuestions";
import { validationSchema } from "./houseContentsProductPriceValidation";

interface Props extends ProductProps {
  shortTitleTexts: Data[];
}

export const HouseContentsProductPrice = ({
  updatePriceRequest,
  shortTitleTexts,
}: Props): React.ReactElement => {
  const dispatch = useDispatch();
  const { getText } = useTexts();
  const handleAddProductToCart = useAddProductToCartHandler(
    HOUSE_CONTENTS_PRICE_STEP.id,
  );
  const { priceCalculation, addProductToCart } = useSelector(
    (state: ReduxState) => state,
  );

  const { housecontents: productCardTexts } = getCollectionOfDittoComponents(
    TextComponentNameLevel1.COVERAGE_CARD,
  );

  const houseContentsProductIntroFormValues = useFormState(
    HOUSE_CONTENTS_INTRO_STEP.id,
  ) as HouseContentsProductIntroFormValues;

  const unableToBuySuper = isFullRental(houseContentsProductIntroFormValues);

  const coverages = getCoveragesFilteredOnStandardSuper(
    !unableToBuySuper,
    HouseContentsCoverage.HOUSE_CONTENTS_STANDARD,
  );

  const initialFormState = getInitialFormStateWithCorrectCoverage(
    useFormState(HOUSE_CONTENTS_PRICE_STEP.id, {
      deductible: getDefaultDeductibleForProduct(
        HouseContentsCoverage.HOUSE_CONTENTS_STANDARD,
      ),
      coverage: unableToBuySuper
        ? HouseContentsCoverage.HOUSE_CONTENTS_STANDARD
        : HouseContentsCoverage.HOUSE_CONTENTS_EXTENDED,
      hasBurglarAlarm: false,
      hasFireAlarm: false,
    }) as HouseContentsProductPriceFormValues,

    unableToBuySuper,
  );

  // ensure the price is always consistent with the form state
  useEffect(() => recalculatePrice(initialFormState), []);

  const recalculatePrice = (formProps: any): void => {
    dispatch(
      updatePriceCalculationFormState({
        stepId: HOUSE_CONTENTS_PRICE_STEP.id,
        formState: {
          ...formProps,
        },
      }),
    );
    updatePriceRequest({
      productPriceUrl: BackendProductInfo.HOUSE_CONTENTS.priceReqUrl,
      updatedProductPriceRequestInfo: {
        hasBurglarAlarm: formProps.hasBurglarAlarm,
        hasFireAlarm: formProps.hasFireAlarm,
      },
    });
  };

  const handleSubmit = (
    formProps: HouseContentsProductPriceFormValues,
  ): void => {
    const productToAdd = {
      coverage: formProps.coverage,
      productType: INSURANCE_NAME_VALUE.HOUSE_CONTENTS,
      filterValue: formProps.deductible,
      hasFireAlarm: formProps.hasFireAlarm,
      hasBurglarAlarm: formProps.hasBurglarAlarm,
    };
    handleAddProductToCart(formProps, [productToAdd]);
  };
  return (
    <Sentry.ErrorBoundary
      beforeCapture={(scope) => {
        scope.setTag(
          "location",
          `CheckPrice - ProductPrice - ${INSURANCE_NAME_VALUE.HOUSE_CONTENTS}`,
        );
      }}
    >
      <ProductPriceTile
        validationSchema={validationSchema()}
        initialValues={initialFormState}
        onSubmit={handleSubmit}
        header={getText("housecontentsform.pageheader.checkprice")}
      >
        {(formProps: FormikProps<HouseContentsProductPriceFormValues>) => {
          return (
            <>
              <Form>
                <SafetyMeasureHouseContentsQuestions
                  formProps={formProps}
                  recalculatePrice={recalculatePrice}
                />
                <ChooseDeductible
                  fieldName="deductible"
                  formProps={formProps}
                  options={getDeductibleOptions(
                    INSURANCE_NAME_VALUE.HOUSE_CONTENTS,
                    coverages,
                  )}
                  label={getText("housecontentsform.label.deductible")}
                  hint={getText("housecontentsform.hint.deductible")}
                />
                <StyledHouseH3>
                  {getText("coveragecard.subheaders.choosecoverage")}
                </StyledHouseH3>

                <HouseContentsPriceElements
                  formProps={formProps}
                  coverages={coverages}
                  unableToBuySuper={unableToBuySuper}
                >
                  {[...coverages]
                    .sort(coveragePriceDifference)
                    .map(([coverage, priceCalculationEntry]) => (
                      <DittoCoverageCard
                        key={coverage}
                        fieldName="coverage"
                        product={ProductBackendName.HOUSE_CONTENTS}
                        coverage={coverage}
                        textComponents={productCardTexts}
                        prices={priceCalculationEntry.prices}
                        priceFilterValue={formProps.values.deductible}
                        priceIsLoading={
                          priceCalculation.priceCalculationResult.isLoading
                        }
                      />
                    ))}
                </HouseContentsPriceElements>
                <BackendErrorAlert
                  priceCalculation={priceCalculation.priceCalculationResult}
                  addProductToCart={addProductToCart}
                />
                {formProps.submitCount > 0 &&
                  Object.keys(formProps.errors).length > 0 && (
                    <ErrorsSummedUp
                      errors={mapValidationErrorsDitto(
                        formProps.errors,
                        shortTitleTexts,
                      )}
                    />
                  )}
                <StyledComparisonWrap>
                  <NavigationButtons
                    isUpsell
                    isLoading={addProductToCart.isLoading}
                  />
                </StyledComparisonWrap>
                <InputFailureTracker
                  productName={INSURANCE_NAME_VALUE.HOUSE_CONTENTS}
                  stepId={HOUSE_CONTENTS_PRICE_STEP.id}
                />
              </Form>
            </>
          );
        }}
      </ProductPriceTile>
    </Sentry.ErrorBoundary>
  );
};
